(() => {
  'use strict';
  if (window.toneAlbums) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const hover = matchMedia('(hover: hover) and (pointer: fine)');
  const controls = new Map();
  const motionOff = () => reduced.matches || document.body.dataset.toneMotion === 'false';
  const intersection = new IntersectionObserver(entries => {
    for (const entry of entries) controls.get(entry.target)?.visible(entry.isIntersecting);
  });

  function init(el) {
    if (!el || el.hasAttribute('data-album-static') || !el.hasAttribute('data-album-vinyl')) return null;
    if (controls.has(el)) return controls.get(el);
    const lifetime = new AbortController();
    let hovered = false, visible = false;
    function sync() {
      const focused = Boolean(el.querySelector('a:focus-visible'));
      const active = visible && !document.hidden && !motionOff() && (hovered && hover.matches || focused);
      el.classList.toggle('is-pulled', active);
    }
    function reset() {
      hovered = false;
      el.classList.remove('is-pulled');
    }
    function destroy() {
      reset();
      lifetime.abort();
      intersection.unobserve(el);
      controls.delete(el);
    }
    el.addEventListener('pointerenter', event => {
      hovered = event.pointerType !== 'touch' && hover.matches;
      sync();
    }, {signal: lifetime.signal});
    el.addEventListener('pointerleave', () => { hovered = false; sync(); }, {signal: lifetime.signal});
    el.addEventListener('pointercancel', reset, {signal: lifetime.signal});
    el.addEventListener('focusin', sync, {signal: lifetime.signal});
    el.addEventListener('focusout', () => queueMicrotask(sync), {signal: lifetime.signal});
    const api = {reset, destroy, sync, visible: value => { visible = value; sync(); }};
    controls.set(el, api);
    intersection.observe(el);
    return api;
  }
  function initAll(root = document) {
    if (root.matches?.('[data-album]')) init(root);
    root.querySelectorAll?.('[data-album]').forEach(init);
  }
  function collect() {
    for (const [el, api] of controls) if (!el.isConnected) api.destroy();
  }
  const settle = () => controls.forEach(api => api.sync());
  reduced.addEventListener('change', settle);
  hover.addEventListener('change', () => controls.forEach(api => api.reset()));
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) controls.forEach(api => api.reset());
    else settle();
  });
  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [el, api] of controls) if (event.target.contains(el)) api.destroy();
  });
  document.addEventListener('tone:content-updated', event => initAll(event.target));
  const observer = new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) initAll(node);
    collect();
  });
  const settings = new MutationObserver(settle);
  initAll();
  observer.observe(document.body, {childList: true, subtree: true});
  settings.observe(document.body, {attributes: true, attributeFilter: ['data-tone-motion']});
  window.toneAlbums = {init, initAll, reset: el => controls.get(el)?.reset()};
  window.addEventListener('pagehide', () => {
    observer.disconnect();
    settings.disconnect();
    controls.forEach(api => api.reset());
  });
  window.addEventListener('pageshow', () => {
    collect();
    initAll();
    observer.observe(document.body, {childList: true, subtree: true});
    settings.observe(document.body, {attributes: true, attributeFilter: ['data-tone-motion']});
    settle();
  });
})();
